/* global PowerPoint Office */

/**
 * PowerPoint.run への薄いラッパー群。
 *
 * 設計方針:
 * - `PowerPoint.run` を直接呼ぶのは本ファイルのみ ([[アドイン開発規則]])
 * - すべて非同期。失敗時は安全な値 (空配列 / null) を返し、呼び出し側で個別に
 *   エラーハンドリングしない方針 (UI 層は欠落データをそのまま受け取って表示)
 */

export type SelectedShape = {
    /** 図形の cNvPr id (Office.js `Shape.id`)。スライド内のみ一意。表示・選択用。 */
    id: string;
    name: string;
    /**
     * 図形の安定一意 ID (`a16:creationId` の GUID、Office.js `Shape.creationId`)。
     * これを `element:<creationId>` 参照に使う ([[2026-06-16 設計ノート スライド一括生成アーキテクチャ]] 問題A)。
     * 取得できない (creationId 未付与の図形 / PowerPointApi 1.10 非対応環境) 場合は `null`。
     */
    creationId: string | null;
};

/** `Shape.creationId` は PowerPointApi 1.10。対応環境でのみ load する。 */
function supportsCreationId(): boolean {
    try {
        return Office.context.requirements.isSetSupported("PowerPointApi", "1.10");
    } catch {
        return false;
    }
}

/**
 * 生成設定が要る最小の API セット。
 *
 * 使っているものと必要な版は、`Slide.tags` / `Shape.tags`（識別子の読み書き＝v8 の設定の土台）が
 * 1.3、`getSelectedShapes` / `setSelectedShapes` / `setSelectedSlides`（選択の取得と移動）が 1.5、
 * `Shape.creationId`（旧鍵の読み取り）が 1.10 である。**選択が取れないと画面Bは成り立たない**ので、
 * 必須は 1.5 と見る。creationId は確認付きで使っているので必須に含めない。
 */
export const REQUIRED_POWERPOINT_API = "1.5";

/**
 * 必須の API セットが使える環境か。
 *
 * マニフェストは `<Requirements>` を宣言していないので、**版が足りない環境でも警告なく読み込まれ、
 * 走査だけが例外で落ちて「紙面を読み取れませんでした」に潰れる**（記録 34）。原因が Office の版だと
 * 画面から分からないため、走査の失敗を描くときにこれを見て言い添える。
 */
export function supportsRequiredApi(): boolean {
    try {
        return Office.context.requirements.isSetSupported("PowerPointApi", REQUIRED_POWERPOINT_API);
    } catch {
        return false;
    }
}

export type CurrentSelection = {
    slideId: string | null;
    /** 現在選択中の図形 (複数選択に対応)。選択なしなら空配列。 */
    shapes: SelectedShape[];
};

/**
 * 現在 PowerPoint 編集ビューで選択されているスライドと図形群を取得する。
 *
 * 複数図形 (Ctrl+クリック / Ctrl+A) を選択している場合、`shapes` に全件が入る。
 * `getSelectedShapes` は PowerPoint API 1.5+ で利用可能。旧バージョンや図形未選択
 * で失敗してもクラッシュせず、その時点までに取れた値を返す。
 */
export async function getCurrentSelection(): Promise<CurrentSelection> {
    const out: CurrentSelection = { slideId: null, shapes: [] };
    try {
        await PowerPoint.run(async (context) => {
            const selectedSlides = context.presentation.getSelectedSlides();
            selectedSlides.load("items/id");
            await context.sync();
            if (selectedSlides.items.length > 0) {
                out.slideId = selectedSlides.items[0].id;
            }
        });
    } catch {
        return out;
    }
    const withCreationId = supportsCreationId();
    try {
        await PowerPoint.run(async (context) => {
            const selectedShapes = context.presentation.getSelectedShapes();
            selectedShapes.load(
                withCreationId ? "items/id,items/name,items/creationId" : "items/id,items/name"
            );
            await context.sync();
            out.shapes = selectedShapes.items.map((s) => ({
                id: s.id,
                name: s.name,
                creationId: withCreationId ? (s.creationId ?? null) : null,
            }));
        });
    } catch {
        // 図形未選択時は API がエラーを返す実装系があるため握りつぶす
    }
    return out;
}

/**
 * 指定スライドをアクティブにする (PowerPointApi 1.5)。
 */
export async function selectSlide(slideId: string): Promise<void> {
    await PowerPoint.run(async (context) => {
        context.presentation.setSelectedSlides([slideId]);
        await context.sync();
    });
}

/**
 * 指定スライドをアクティブにし、その上の図形を選択する (PowerPointApi 1.5)。
 *
 * `setSelectedShapes` はアクティブスライド上でのみ有効なため、
 * 先にスライドを切り替えてから図形を選択する。
 */
export async function selectShapeOnSlide(slideId: string, shapeId: string): Promise<void> {
    await PowerPoint.run(async (context) => {
        context.presentation.setSelectedSlides([slideId]);
        await context.sync();
        const slide = context.presentation.slides.getItem(slideId);
        slide.setSelectedShapes([shapeId]);
        await context.sync();
    });
}

/**
 * 指定スライド上の図形の選択を解く (PowerPointApi 1.5)。
 *
 * **いま選ばれているのがその図形 1 つだけのときに限って解く。** 呼び手（画面B のアコーディオン）が
 * 枠を選んだあと、利用者が紙面で別の枠を直に押していることがある。無条件に消すと、利用者が
 * 自分で選んだものを画面が黙って捨てることになる。
 *
 * 判定と解除を 1 つの `PowerPoint.run` に入れるのは、読んでから解くまでの間に選択が動く隙を
 * 作らないためで、往復も 1 回で済む。判定に `useCurrentSelection` のキャッシュを使わないのは、
 * あれが `DocumentSelectionChanged` を受けてから非同期に追いつくためで、開いてすぐ閉じた場合に
 * 自分で選んだ直後の値が届いておらず、解除が効かなくなる。
 *
 * 失敗しても投げない。選択は見せ方であって設定の保存ではなく、図形未選択時に
 * `getSelectedShapes` が例外を返す実装系があるため（`getCurrentSelection` と同じ理由）、
 * ここで上げると「解くものが無い」だけで画面にエラーが出る。
 */
export async function releaseShapeSelection(slideId: string, shapeId: string): Promise<void> {
    try {
        await PowerPoint.run(async (context) => {
            const slides = context.presentation.getSelectedSlides();
            const shapes = context.presentation.getSelectedShapes();
            slides.load("items/id");
            shapes.load("items/id");
            await context.sync();

            // 図形 id は枚の中でだけ一意なので、枚も併せて確かめる。
            const onSameSlide = slides.items.length === 1 && slides.items[0].id === slideId;
            const onlyThatShape = shapes.items.length === 1 && shapes.items[0].id === shapeId;
            if (!onSameSlide || !onlyThatShape) return;

            context.presentation.slides.getItem(slideId).setSelectedShapes([]);
            await context.sync();
        });
    } catch {
        // 解けなかっただけ。選択はそのまま残る。
    }
}
